import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '../api.js';
import { useSession, useTheme } from '../session.js';

interface DemoAccount { name: string; email: string; role: string; initials: string; note: string }

export function Login(): JSX.Element {
  const { login } = useSession();
  const [theme, toggleTheme] = useTheme();
  const accounts = useQuery<DemoAccount[]>('/demo-accounts');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (withEmail = email, withPassword = password) => {
    setBusy(true);
    setError(null);
    try {
      await login(withEmail, withPassword);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <header className="topbar">
        <Link to="/" className="topbar__brand" style={{ color: '#fff', textDecoration: 'none' }}>
          <span className="topbar__mark" />
          FlightSlots
        </Link>
        <button className="chip" onClick={toggleTheme}>{theme === 'dark' ? 'Light' : 'Dark'}</button>
      </header>

      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 20px' }}>
        <div style={{ width: '100%', maxWidth: 880, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 20 }}>
          <div className="card">
            <div className="card__head"><span className="card__title">Sign in</span></div>
            <form
              className="card__body stack"
              onSubmit={(e) => { e.preventDefault(); submit(); }}
            >
              <label className="field">
                <span>Email</span>
                <input className="input" type="email" autoComplete="username" value={email}
                  onChange={(e) => setEmail(e.target.value)} required />
              </label>
              <label className="field">
                <span>Password</span>
                <input className="input" type="password" autoComplete="current-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required />
              </label>
              {error && (
                <div style={{ padding: 8, background: 'var(--conflict-bg)', border: '1px solid var(--conflict-bd)', color: 'var(--conflict-c)', borderRadius: 6, fontSize: 12.5 }}>
                  {error}
                </div>
              )}
              <button className="btn btn--primary btn--block" type="submit" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
              <p className="small muted" style={{ margin: 0, textAlign: 'center' }}>
                <Link to="/reset">Forgot your password?</Link>
              </p>
              <p className="small muted" style={{ margin: 0, textAlign: 'center' }}>
                New here? <Link to="/signup">Set up a school</Link>
                {' or '}<Link to="/signup?code=">join one with a code</Link>.
              </p>
            </form>
          </div>

          {/* Absent on a deployed school, where these accounts are not offered. */}
          {!!accounts.data?.length && (
          <div className="card">
            <div className="card__head">
              <span className="card__title">Or open a demo account</span>
            </div>
            <div className="small muted" style={{ padding: '8px 10px' }}>
              The same school, seen from four desks. Each role enforces its own permissions on the server.
            </div>
            {accounts.data?.map((a) => (
              <button
                key={a.email}
                onClick={() => submit(a.email, 'flightslots')}
                disabled={busy}
                style={{
                  display: 'flex', gap: 10, alignItems: 'flex-start', width: '100%', textAlign: 'left',
                  padding: '10px 12px', background: 'transparent', border: 0,
                  borderTop: '1px solid var(--border-3)',
                }}
              >
                <span className="avatar">{a.initials}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontWeight: 700, fontSize: 12.5 }}>
                    {a.name} · {a.role}
                  </span>
                  <span className="small muted" style={{ display: 'block', textWrap: 'pretty' }}>{a.note}</span>
                </span>
              </button>
            ))}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
